import type { BattleClaimStatus, BattleTargetClaimStatus } from './types';

/**
 * Read path for the active battle segment. Coin-scoped like the tournament read, with the player
 * resolved from the auth token rather than the url. The version prefix is NOT written here — the
 * axios request interceptor prefixes `/v1/` onto every non-`/v2` url.
 *
 * NOTE: player-backend has not shipped this route yet; the exact spelling is the plan's proposal and
 * lives in one constant so a rename is a one-line change.
 */
export const SLOT_BATTLE_CURRENT_PATH = 'slot-battle/current';

/**
 * Claim (or acknowledge) the settled closing: `POST slot-battle/claim/:runKey/:segmentKey`, no body.
 * Like the read, the player comes from the auth token — the path never names them.
 */
export const SLOT_BATTLE_CLAIM_PATH = 'slot-battle/claim';

/** Every status the claim may answer; anything else is treated as a failed claim. */
export const SLOT_BATTLE_CLAIM_STATUSES = [
  'claimed',
  'acknowledged',
  'already_claimed',
  'nothing_to_claim',
  'expired',
  'not_settled',
] as const satisfies readonly BattleClaimStatus[];

/**
 * Claim the goal reward while the battle is live: `POST slot-battle/claim-target/:runKey/:segmentKey`,
 * no body. The player comes from the auth token, as for the closing's claim.
 */
export const SLOT_BATTLE_CLAIM_TARGET_PATH = 'slot-battle/claim-target';

/** Every status the goal-reward claim may answer; anything else is treated as a failed claim. */
export const SLOT_BATTLE_TARGET_CLAIM_STATUSES = [
  'claimed',
  'already_claimed',
  'not_reached',
  'not_live',
  'excluded',
  'nothing_to_claim',
] as const satisfies readonly BattleTargetClaimStatus[];

/** Global kill switch. Off means every surface disappears — widget, dialog and all. */
export const SLOT_BATTLE_FLAG = 'slot_battle_feature';

/**
 * The per-run flag's no-match variation. Ops' run flag serves the SEGMENT KEY as the variation
 * value; `filter` (or an absent flag) means this player belongs to no segment of the run.
 */
export const SLOT_BATTLE_NO_SEGMENT_VARIATION = 'filter';

/** Collapses a page load's burst of `fetch()` calls into one request. Same trick as the tournament read. */
export const SLOT_BATTLE_FETCH_COALESCE_MS = 750;

/** While the dialog is open the standings refresh on their own; core caches the page for 5 s too. */
export const SLOT_BATTLE_LIVE_POLL_MS = 5_000;

/**
 * While settling, keep reading even with the dialog closed, so the closing pane arrives without a reload.
 * A live battle whose end has passed is read at the same pace until the server answers `settling`.
 */
export const SLOT_BATTLE_SETTLING_POLL_MS = 30_000;

/**
 * From the battle's end until its closing arrives, read this often, dialog open or not, so the win
 * popup opens by itself within seconds of settlement (core settles at end + a 20 s buffer).
 */
export const SLOT_BATTLE_RESULTS_POLL_MS = 2_500;

/**
 * How long after the end the results read keeps that pace. Past it, a run still settling is read at
 * the settling pace: core is late, and thousands of clients reading every 2.5 s would not hurry it.
 */
export const SLOT_BATTLE_RESULTS_WINDOW_MS = 90_000;

/**
 * An upcoming battle whose start has passed is re-read this often: core caches the page for 5 s, so
 * the read at the start can still be the pre-start answer.
 */
export const SLOT_BATTLE_START_RETRY_POLL_MS = 5_000;

/**
 * How many answers in a row that still say `upcoming` after the start are re-read at that pace. Past
 * it, a client clock running ahead of core's is read at the settling pace until the run goes live.
 */
export const SLOT_BATTLE_START_RETRY_LIMIT = 6;

export { MAX_TIMEOUT_MS } from '../../utils/time';
